'use client';

import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ControlledPasswordInput } from '@/components/auth/controlled-password-input';
import { OtpInput } from '@/components/auth/otp-input';
import { authClient } from '@/lib/auth-client';
import { useLoadingCallback } from '@/lib/hooks/use-loading-callback';
import { authToasts } from '@/lib/utils/toast';
import { verifyOtpSchema } from '@/schemas/auth';

interface ResetPasswordFormProps {
    email: string;
    onBackToEmail: () => void;
}

export function ResetPasswordForm({
    email,
    onBackToEmail,
}: ResetPasswordFormProps) {
    const router = useRouter();

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors, isValid },
    } = useForm<z.infer<typeof verifyOtpSchema>>({
        resolver: zodResolver(verifyOtpSchema),
        mode: 'onChange', // Real-time validation
        defaultValues: {
            email,
        },
    });

    const [handleVerifyOtp, isResetting] = useLoadingCallback(
        async (data: z.infer<typeof verifyOtpSchema>) => {
            const response = await authClient.emailOtp.resetPassword({
                email: data.email,
                otp: data.otp,
                password: data.password,
            });

            if (response.error) {
                authToasts.passwordResetFailed(
                    response.error.message ||
                        'The verification code is incorrect or has expired.'
                );
                return;
            }

            authToasts.passwordResetSuccess();
            await new Promise((resolve) => setTimeout(resolve, 2000));
            router.push('/auth/login');
        }
    );

    const [handleResendOtp, isResending] = useLoadingCallback(async () => {
        const response = await authClient.emailOtp.sendVerificationOtp({
            email,
            type: 'forget-password',
        });

        if (response.error) {
            authToasts.passwordResetError(
                response.error.message || 'Failed to send verification code.'
            );
            return;
        }

        // Clear the OTP field when resending
        setValue('otp', '');
        authToasts.passwordResetCodeResent();
    });

    return (
        <div className="flex flex-col gap-4">
            <div className="text-center">
                <p className="mb-4 text-sm text-zinc-400">
                    We sent a 6-digit code to <strong>{email}</strong>. The code
                    will expire in 10 minutes.
                </p>
            </div>
            <Form
                className="flex w-full flex-col gap-4"
                onSubmit={handleSubmit(handleVerifyOtp)}
            >
                <input type="hidden" {...control.register('email')} />

                <div className="flex w-full justify-center">
                    <OtpInput
                        errorMessage={errors.otp?.message}
                        isInvalid={!!errors.otp}
                        length={6}
                        {...control.register('otp')}
                    />
                </div>

                <ControlledPasswordInput
                    autoComplete="new-password"
                    control={control}
                    label="New Password"
                    name="password"
                />

                <Button
                    color="primary"
                    fullWidth
                    isDisabled={!isValid}
                    isLoading={isResetting}
                    type="submit"
                >
                    Reset Password
                </Button>

                <div className="flex w-full flex-row gap-2">
                    <Button
                        fullWidth
                        isLoading={isResending}
                        onPress={handleResendOtp}
                        variant="faded"
                    >
                        Resend Code
                    </Button>

                    <Button
                        fullWidth
                        onPress={onBackToEmail}
                        variant="bordered"
                    >
                        Change Email Address
                    </Button>
                </div>
            </Form>
        </div>
    );
}
